import React, { useEffect } from "react";

import Image from "next/image";

import { Alert, Box, Button, Container, Grid, Typography } from "@mui/material";

import useCountDown from "react-countdown-hook";
import { FormProvider, useForm } from "react-hook-form";

import DecorImage from "../../../public/static/creator/join-us/props.png";
import {
  StandardCheckbox,
  StandardRadioGroup,
  StandardTextField,
} from "../../components/standard-form-fields";
import useFormPersist from "../../hooks/useFormPersist";
import {
  ComicArtistSubsection,
  IllustratorSubsection,
  MusicProducerSubsection,
  OtherSubsection,
  VideoProducerSubsection,
  WriterSubsection,
} from "./form-subsections";

const FORM_INITIAL_DEFAULT_VALUES = {};

const SuccessAlert = () => (
  <Alert severity="success">
    感謝您，我們已經收到您的資料，您將於
    <Typography display="inline" variant="body2" fontWeight={600}>
      數分鐘內收到系統的自動確認電郵
    </Typography>
    ，您可於電郵內閱覽確認您填寫的內容，我們將儘快處理您的申請!
  </Alert>
);

const JoinUsView = ({
  formDefaultValues = FORM_INITIAL_DEFAULT_VALUES,
  loading,
  onFormDirtyChanged,
  onSubmit,
  submitted,
  ...props
}) => {
  const formMethods = useForm({ defaultValues: formDefaultValues });
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isDirty },
  } = formMethods;
  useFormPersist("form-creator-joinus", { watch, reset, setValue });

  const [cooldown, { start: startTimer }] = useCountDown(0);
  const internalOnSubmit = (...submitProps) => {
    startTimer(30000);
    onSubmit(...submitProps);
  };

  useEffect(() => {
    if (onFormDirtyChanged) onFormDirtyChanged(isDirty);
  }, [isDirty, onFormDirtyChanged]);

  const creatorType = watch("creatorType");

  return (
    <Box
      sx={{
        backgroundColor: "background.paper",
        pt: 2,
        pb: 16,
      }}
      {...props}
    >
      <Container maxWidth="lg">
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Box sx={{ mx: "auto", my: 2, width: "80%" }}>
              <Image src={DecorImage} layout="responsive" placeholder="empty" />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box mb={2}>
              <Typography variant="h5">創作者申請</Typography>
            </Box>
            <Box>
              <Typography variant="caption">
                * 未填完的資料會暫時儲存在您的電腦
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption">
                *
                資料提交後亦仍然會儲存在您的電腦，以便您日後提交修改（只限增加項目或修改資料，如需刪除項目請聯絡我們）
              </Typography>
            </Box>
            <Box mb={4}>
              <Typography variant="caption">
                *
                提交資料後，您會於數分鐘內收到系統的自動確認電郵，【創作者事務所】將會審查你的申請。通過申請的創作者將在五至六個工作天內會有專人與你聯繫，並且每當有適合你的商業委託出現，你都將會收到通知。如果這一次的申請失敗，之後也歡迎再次提交申請。
              </Typography>
            </Box>
            {submitted && <SuccessAlert />}
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(internalOnSubmit)}>
                <Grid container spacing={3}>
                  <StandardTextField
                    name="name"
                    required
                    label="名稱 / 稱呼"
                    placeholder="您的名稱 / 稱呼"
                    registerProps={{
                      required: "請提供稱呼以便聯絡",
                    }}
                  />
                  <StandardTextField
                    name="email"
                    required
                    label="聯絡電郵"
                    placeholder="方便聯絡用的電郵地址"
                    registerProps={{
                      required: "請提供電郵地址以便聯絡",
                      pattern: {
                        value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,})+$/,
                        message: "您的電郵地址可能有誤，請檢查後再試一次",
                      },
                    }}
                  />
                  <StandardTextField
                    name="username"
                    required
                    label="飛天奶茶帳號名稱"
                    placeholder="飛天奶茶帳號名稱"
                    registerProps={{
                      required: "請提供飛天奶茶帳號名稱",
                    }}
                  />
                  <StandardTextField
                    name="sns"
                    label="個人專頁/社交媒體帳號"
                    placeholder="例如Facebook, Instagram, pixiv, Twitter"
                  />
                  <StandardRadioGroup
                    name="invited"
                    choices={[
                      { value: "true", label: "有" },
                      { value: "false", label: "沒有" },
                    ]}
                    required
                    label="飛天奶茶官方邀請"
                    registerProps={{
                      required: "請告訴我們您有否受到飛天奶茶官方邀請",
                    }}
                  />
                  <StandardCheckbox
                    name="creatorType"
                    choices={[
                      { name: "illustration", label: "繪師" },
                      { name: "comic", label: "漫畫家" },
                      { name: "novel", label: "作家" },
                      { name: "video", label: "影片製作者" },
                      { name: "music", label: "音樂人" },
                      { name: "other", label: "其他" },
                    ]}
                    required
                    label="屬於哪種創作者"
                  />
                  {creatorType?.comic && <ComicArtistSubsection />}
                  {creatorType?.illustration && <IllustratorSubsection />}
                  {creatorType?.music && <MusicProducerSubsection />}
                  {creatorType?.other && <OtherSubsection />}
                  {creatorType?.video && <VideoProducerSubsection />}
                  {creatorType?.novel && <WriterSubsection />}

                  <Grid item xs={12}>
                    {submitted && <SuccessAlert />}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      type="submit"
                      color="primary"
                      disabled={loading || cooldown}
                      fullWidth
                      sx={{ minWidth: "100px" }}
                      variant="contained"
                    >
                      {(() => {
                        if (loading) {
                          return "正在提交...";
                        }
                        if (cooldown) {
                          return `已經提交，請稍後再更新...(${(
                            cooldown / 1000
                          ).toFixed(0)})`;
                        }
                        return "提交";
                      })()}
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      onClick={() => reset()}
                      disabled={loading}
                      fullWidth
                      sx={{ minWidth: "100px" }}
                      variant="outlined"
                    >
                      清除
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default JoinUsView;
